import React, { useEffect, useContext } from 'react';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { connect } from 'react-redux';
import isEqual from 'lodash.isequal';
import PageTitle from '../../../shared/components/PageTitle';
import { TabContent } from '../../../routes/RouterOutlet.styles';
import { personalDetailsSelectors } from '../../PersonalDetails/ducks';
import { notificationSettingsOperations, notificationSettingsSelectors } from '../ducks';
import EmailNotifications from './EmailNotifications';
import PhoneNotifications from './PhoneNotifications';
import CreditMonitoring from './CreditMonitoring';
import { sharedSelectors } from '../../../shared/ducks';
import LoadingPage from '../../../shared/components/LoadingPage';
import ErrorPage from '../../../shared/components/ErrorPage';
import { NotificationSettingsFeatureFlags } from '../../../contracts';
import type { RootState } from '../../../store/types';

type StateProps = {
  emailAddress: string;
  hasFetchedNotifications: boolean;
  hasFetchedSoftpullConsent: boolean;
  isLoadingNotifications: boolean;
  isLoadingPersonalDetails: boolean;
  isLoadingSoftpullConsent: boolean;
  notificationSettingsFeatureFlags: NotificationSettingsFeatureFlags;
  notificationsError: Record<string, unknown>;
  personalDetailsError: Record<string, unknown>;
  phoneNumber: string;
  softpullConsentError: Record<string, unknown>;
  userId: string;
};

type DispatchProps = {
  fetchAllConsents: (userId: string) => void;
};

function ShowNotificationSettings({
  fetchAllConsents,
  hasFetchedNotifications,
  hasFetchedSoftpullConsent,
  phoneNumber,
  emailAddress,
  userId,
  notificationSettingsFeatureFlags,
  isLoadingNotifications,
  isLoadingPersonalDetails,
  isLoadingSoftpullConsent,
  notificationsError,
  personalDetailsError,
  softpullConsentError
}: StateProps & DispatchProps) {
  useEffect(() => {
    if (!hasFetchedNotifications && !hasFetchedSoftpullConsent && userId) {
      fetchAllConsents(userId);
    }
  }, [hasFetchedNotifications, hasFetchedSoftpullConsent, userId]);
  const { trackEvent } = useContext(AnalyticsContext);
  const { shouldShowEmail } = notificationSettingsFeatureFlags;
  const isLoading = isLoadingPersonalDetails || isLoadingNotifications || isLoadingSoftpullConsent;
  const hasError =
    !isEqual(notificationsError, {}) ||
    !isEqual(personalDetailsError, {}) ||
    !isEqual(softpullConsentError, {});

  const shouldShowError = !isLoading && hasError;
  const shouldShowComponent = !isLoading && !shouldShowError;

  const trackEventable = (message: boolean, customAttributes: Record<string, string>) => {
    trackEvent(
      `user platform - account - notification settings - toggle ${message ? 'on' : 'off'}`,
      customAttributes
    );
  };

  return (
    <>
      <PageTitle title="NOTIFICATIONS & SETTINGS" className="has-subtitle" data-qa="page-title" />
      {isLoadingNotifications && <LoadingPage />}
      {shouldShowError && <ErrorPage data-qa="error-page" data-testid="error-page" />}
      {shouldShowComponent && (
        <TabContent data-qa="page-content-wrap" data-testid="page-content-wrap">
          {phoneNumber && (
            <PhoneNotifications
              notificationSettingsFeatureFlags={notificationSettingsFeatureFlags}
              trackEventable={trackEventable}
            />
          )}
          {shouldShowEmail && <EmailNotifications trackEventable={trackEventable} />}
          <CreditMonitoring trackEventable={trackEventable} />
        </TabContent>
      )}
    </>
  );
}

const mapDispatchToProps = {
  fetchAllConsents: notificationSettingsOperations.fetchAllConsents
};

const mapStateToProps = (state: RootState) => ({
  userId: personalDetailsSelectors.getUserId(state),
  emailAddress: personalDetailsSelectors.getEmailAddress(state),
  phoneNumber: personalDetailsSelectors.getPhoneNumber(state),
  hasFetchedNotifications: notificationSettingsSelectors.getHasFetchedNotifications(state),
  hasFetchedSoftpullConsent: notificationSettingsSelectors.getHasFetchedSoftpullConsent(state),
  isUpdatingNotifications: notificationSettingsSelectors.getIsUpdatingNotifications(state),
  notificationSettingsFeatureFlags: sharedSelectors.getNotificationSettingsFeatureFlags(state),
  isLoadingNotifications: notificationSettingsSelectors.getIsLoadingNotifications(state),
  isLoadingPersonalDetails: personalDetailsSelectors.getIsLoadingPersonalDetails(state),
  isLoadingSoftpullConsent: notificationSettingsSelectors.getIsLoadingSoftpullConsent(state),
  notificationsError: notificationSettingsSelectors.getNotificationsError(state),
  personalDetailsError: personalDetailsSelectors.getPersonalDetailsError(state),
  softpullConsentError: notificationSettingsSelectors.getSoftpullConsentError(state)
});

export default connect(mapStateToProps, mapDispatchToProps)(ShowNotificationSettings);
